// ft3d-hud/chat.ts — FT3D table chat: the command line under the log (owner 2026-09-15).
//
// WoW's chat frame, in our HUD. Enter (or clicking the strip) opens the input; Enter sends; Esc
// cancels; ↑/↓ recall what you typed. A slash command chooses the channel (chat-commands.ts), and the
// current one is named in the chip to the LEFT of the field.
//
// Speech shares the LOG's line stream (owner 2026-09-16) so events and talk interleave in time, and the
// feed's All / Chat / Combat tabs decide what is shown — but every sweep in log.ts is scoped to
// `:not(.is-chat)`, so a busy fight never trims speech away and "log off" never takes chat with it.
// This module keeps no roll model, opens no socket and writes nothing: it POSTs to
// options/game/encounter/ft3d_chat_inc.php, which saves the browser's hearing/reveal plan and
// returns the stored line. This browser then sends it through the page's socket bridge.
//
// Every line lands here by exactly one path — deliver() — whether it came from our own POST response
// or from the socket, deduplicated by message_id, so the sender sees their line instantly and never twice.

import { parseChatInput, channelLabel, chatHelpText, chatLegend, type FtChatChannel } from './chat-commands';
import { ft3dLogLineHost } from './log';
import { chatVisibleToViewer } from '../janus-engine/presentation/chat';

interface ChatSpeaker { kind?: string; entity_id?: string | null; name?: string; pic_url?: string | null; voice?: string | null }
export interface FtChatMessage {
    schema?: string;
    message_id?: string;
    campaign_id?: string;
    encounter_id?: string | null;
    channel?: string;
    emote?: string | null;
    text?: string;
    speaker?: ChatSpeaker;
    from_user_id?: string;
    recipient_name?: string | null;
    ts?: number;
    /** /dance: the speaker's ordered dance playlist (server-read from their action-bar settings). */
    dances?: string[];
    recipient_ids?: string[];
}

const CHAT_SCHEMA = 'ft.ft3d.chat.v1';
/** The only channels a frame may claim. An unknown one is dropped, never defaulted into `say`. */
const CHANNELS = ['say', 'yell', 'emote', 'ooc', 'whisper', 'gm'] as const;
/** Channels that speak from a token. An ALLOW-list: a new private channel must not inherit a bubble. */
const BUBBLE_CHANNELS = ['say', 'yell', 'emote'];
/** A line stays readable this long before it fades out of the resting transcript. */
const LINE_MS = 18000;
const FADE_OUT_MS = 700;
/** Chat's own budget inside the shared stream — the log trims ROLL lines against its own cap. */
const MAX_LINES = 60;
const HISTORY_KEY = 'ft3d.chat.draftHistory';

let root: HTMLElement | null = null;
let linesEl: HTMLElement | null = null;
let inputEl: HTMLInputElement | null = null;
let chipEl: HTMLElement | null = null;
let menuEl: HTMLElement | null = null;
/** The whole feed (tabs + stream + bar) — hovering any of it holds a fading line on screen. */
let feedEl: HTMLElement | null = null;
let open = false;
let stickyChannel: FtChatChannel = 'say';
let stickyTarget = '';
const seen = new Set<string>();
const sentHistory: string[] = [];
let historyIndex = -1;
/** What the player was typing when they started walking back through ↑ history. */
let recallDraft = '';
/** Where the history load inserts: the first live line at the moment history arrives (see appendLine). */
let historyAnchor: ChildNode | null = null;
let historyAnchorSet = false;

/** A receipt a smoke (or a person in devtools) can read — never a console error in normal play. */
function ftChatDebug(entry: Record<string, unknown>): void {
    const w = window as { __ftChat?: Record<string, unknown>[] };
    w.__ftChat = w.__ftChat ?? [];
    w.__ftChat.push({ at: Date.now(), ...entry });
    if (w.__ftChat.length > 40) w.__ftChat.shift();
}

function campaignId(): string {
    return String((window as { FateTide?: { campaign?: { id?: string } } }).FateTide?.campaign?.id ?? '').trim();
}

function encounterId(): string {
    const w = window as { ftGetActiveEncounterId?: () => string; ftFt3dEncounterId?: () => string };
    const active = String(w.ftGetActiveEncounterId?.() ?? '').trim();
    // The FT3D-only campaign surface can have no ACTIVE encounter while the scene plainly shows one;
    // my_campaign.php publishes its frame-derived id for exactly this case (same fallback as the ping).
    return active || String(w.ftFt3dEncounterId?.() ?? '').trim();
}

/**
 * WHO IS TYPING. The same chain the FT3D shell uses for every other action (my_campaign.php's
 * `context.caster_id || ft3dActorId`): the active caster, then the sidelog context, then the actor the
 * shell has selected, and finally the player's own character. Sending a blank left the server to guess,
 * and its guess — the first creature in the room this account may control — is frequently NOT the
 * creature you are playing (owner 2026-09-16).
 */
function activeCasterId(): string {
    const w = window as {
        ftGetActiveCasterId?: () => string;
        getSidelogContext?: () => { caster_id?: string };
        ftFt3dActorId?: () => string;
        ftGetMasterCasterId?: () => string;
    };
    const candidates = [
        w.ftGetActiveCasterId?.(),
        w.getSidelogContext?.()?.caster_id,
        w.ftFt3dActorId?.(),
        w.ftGetMasterCasterId?.(),
    ];
    for (const candidate of candidates) {
        const id = String(candidate ?? '').trim();
        if (id) return id;
    }
    return '';
}

/**
 * WHO PERFORMS an emote the instant it is sent — the speaker the server will resolve, decided before it
 * answers. The chosen caster when the page has one; otherwise the server's fallback, mirrored from the
 * page's own roster: this account's PLAYER CHARACTER in the room, else the first other creature it
 * controls (options/game/encounter/ft3d_chat_inc.php). A manager with nothing selected speaks as the GM,
 * who has no body to gesture with — '' here, and the server's frames stay the authority.
 */
function emoteSpeakerId(): string {
    type Row = Record<string, unknown>;
    const enc = (window as { FateTide?: { encounterArr?: { encounters?: { encounter_id?: unknown }; encounter_creatures_arr?: unknown } } })
        .FateTide?.encounterArr;
    // The roster must be THIS room's: the server resolves the speaker against the encounter the line is sent to.
    const here = encounterId().toUpperCase();
    if (!enc || (here && String(enc.encounters?.encounter_id ?? '').toUpperCase() !== here)) return '';
    const raw = enc.encounter_creatures_arr;
    const rows = (Array.isArray(raw) ? raw : raw && typeof raw === 'object' ? Object.values(raw) : []) as Row[];
    const ecOf = (row: Row) => (row.encounter_creatures ?? {}) as { encounter_creature_id?: unknown; item_type?: unknown; encounter_creature_private?: unknown };
    const idOf = (row: Row) => String(ecOf(row).encounter_creature_id ?? '').trim().toUpperCase();
    const canPlay = (row: Row) => ((row.permissions ?? {}) as { player?: unknown }).player === true;
    // Server: `item_field($row, 'item_type') ?: item_field(<encounter_creatures>, 'item_type')` — a TRUTHY fallback.
    const typeOf = (row: Row) => String(((row.items ?? {}) as { item_type?: unknown }).item_type || ecOf(row).item_type || '');

    // 1. A valid chosen caster speaks: in this room, and this account may play it.
    const caster = activeCasterId().toUpperCase();
    let speaker: Row | null = caster ? rows.find(row => idOf(row) === caster && canPlay(row)) ?? null : null;
    // 2. Otherwise a manager speaks as the GM — no body — and anyone else as their own PC, then any creature they control.
    if (!speaker) {
        // The page's own server-rendered manager flag (my_campaign.php `ft3dCanManage`).
        const manager = document.querySelector('[data-ft3d-can-manage]')?.getAttribute('data-ft3d-can-manage') === '1';
        if (manager) return '';
        const controlled = rows.filter(row => canPlay(row) && idOf(row));
        speaker = controlled.find(row => typeOf(row) === 'player_character') ?? controlled[0] ?? null;
    }
    if (!speaker) return '';
    // 3. The server REFUSES a staged creature on an in-character channel, and makes a HIDDEN speaker wait for its
    //    reveal (fail-closed). Neither is performed ahead of the server: a hidden speaker's gesture arrives on the
    //    server's own cue, after the reveal has stuck.
    // PHP `!empty()` truthiness: a serialized '0' is PUBLIC, as the server reads it.
    const priv = ecOf(speaker).encounter_creature_private;
    if (priv !== undefined && priv !== null && priv !== false && priv !== 0 && priv !== '' && priv !== '0') return '';
    if (rowIsHidden(speaker)) return '';
    return idOf(speaker);
}

/** The `hidden` stance (status item 47667406-…), read the way targeting-combat.ts's checkTargetHidden() does. */
const HIDDEN_STATUS_ID = '47667406-1B72-9D60-310B-D8CF6BFE397D';
function rowIsHidden(row: Record<string, unknown>): boolean {
    const reader = (window as { ftCreatureStatuses?: (row: unknown, mode: string) => unknown }).ftCreatureStatuses;
    let list: unknown = typeof reader === 'function' ? reader(row, 'target') : ((row.statuses ?? {}) as { target?: unknown }).target;
    if (!Array.isArray(list)) list = list ? [list] : [];
    return (list as unknown[]).some((entry) => {
        const e = (entry ?? {}) as Record<string, unknown>;
        const items = (e.items ?? {}) as { item_id?: unknown };
        const statusItems = ((e.status_arr ?? {}) as { items?: { item_id?: unknown } }).items ?? {};
        const id = String(e.status_item_id || items.item_id || statusItems.item_id || e.item_id || '').toUpperCase();
        return id === HIDDEN_STATUS_ID;
    });
}

/** Party names, so `/w Delion Test hello` finds a two-word target without quotes. */
function rosterNames(): string[] {
    const names = new Set<string>();
    document.querySelectorAll<HTMLElement>('.ft3d-party-member__name').forEach((el) => {
        const name = (el.textContent ?? '').trim();
        if (name) names.add(name);
    });
    return [...names];
}

interface FtChatLineTimers { __ftChatExpiry?: number; __ftChatFade?: number }

function clearExpiry(line: HTMLElement): void {
    const timers = line as unknown as FtChatLineTimers;
    // BOTH timers: the wait, and the fade-out that follows it. Cancelling only the first left a
    // pending callback that could still hide a line — and hold a removed node — after the reader
    // came back (Codex review 2026-09-16).
    if (timers.__ftChatExpiry !== undefined) {
        window.clearTimeout(timers.__ftChatExpiry);
        delete timers.__ftChatExpiry;
    }
    if (timers.__ftChatFade !== undefined) {
        window.clearTimeout(timers.__ftChatFade);
        delete timers.__ftChatFade;
    }
}

/** Someone is reading: the bar is open, or the pointer is anywhere on the feed. */
function beingRead(): boolean {
    return open || feedEl?.matches(':hover') === true || root?.matches(':hover') === true;
}

function scheduleExpiry(line: HTMLElement): void {
    clearExpiry(line);
    const timer = window.setTimeout(() => {
        // ⛔ A detached line STOPS. Rescheduling one kept a timer chain alive for every trimmed row,
        // holding the node and accumulating timers for as long as the page stayed open.
        if (!line.isConnected) return;
        // Pointing at the FEED (not just the bar) counts as reading it — the lines live in the log's
        // stream now, so that is the element the reader's cursor is actually over.
        if (beingRead()) {
            // Reading (or typing) holds the transcript open; try again once they are done.
            scheduleExpiry(line);
            return;
        }
        line.classList.add('is-leaving');
        const fade = window.setTimeout(() => {
            if (!line.isConnected) return;
            // The reader can arrive DURING the fade. Give the line back rather than finishing a
            // disappearance they interrupted.
            if (beingRead()) {
                line.classList.remove('is-leaving');
                line.classList.add('is-shown');
                scheduleExpiry(line);
                return;
            }
            if (line.classList.contains('is-leaving')) line.classList.remove('is-shown');
        }, FADE_OUT_MS);
        (line as unknown as FtChatLineTimers).__ftChatFade = fade;
    }, LINE_MS);
    (line as unknown as FtChatLineTimers).__ftChatExpiry = timer;
}

/** OUR lines in the shared stream. The log owns everything without `.is-chat`. */
function chatLinesInStream(): HTMLElement[] {
    if (!linesEl) return [];
    return Array.from(linesEl.querySelectorAll<HTMLElement>('.ft3d-hud__line.is-chat'));
}

function trim(): void {
    // ⛔ Never evict while someone is reading — the 61st message would pull the row they are on out
    // from under them. The stream grows a little; setOpen(false) trims once they are done.
    if (beingRead()) return;
    let lines = chatLinesInStream();
    while (lines.length > MAX_LINES && lines[0]) {
        clearExpiry(lines[0]);
        lines[0].remove();
        lines = chatLinesInStream();
    }
}

/**
 * `atTop` is for HISTORY, which can land AFTER a live line has already arrived. Appending it would put
 * older messages under newer ones and let the trim drop the newest line the viewer just saw; inserting
 * above the existing lines keeps the transcript chronological either way.
 */
function appendLine(build: (line: HTMLElement) => void, channel: string, atTop = false): HTMLElement | null {
    if (!linesEl) return null;
    const line = document.createElement('p');
    // BOTH class families: `ft3d-hud__line` so it sits in the shared stream like any event, `is-chat`
    // so every sweep in log.ts knows to leave it alone, and `ft3d-chat__line` for speech styling.
    line.className = 'ft3d-hud__line is-chat ft3d-chat__line';
    // ⛔ NOT `data-ft3d-chat-channel` — that attribute belongs to the channel CHIP, and naming a line
    // the same made `querySelector('[data-ft3d-chat-channel]')` return a <p> line instead of the
    // button (7 matches on a live page, 2026-09-16). One attribute, one meaning.
    line.dataset.ft3dChatLineChannel = channel;
    line.dataset.ft3dLogKind = 'chat';
    build(line);
    if (atTop) {
        // The anchor is the first LIVE line, captured once (null = nothing live yet, so history simply
        // appends). Inserting every history row before that same anchor keeps history in its own order.
        if (!historyAnchorSet) {
            historyAnchor = linesEl.firstChild;
            historyAnchorSet = true;
        }
        linesEl.insertBefore(line, historyAnchor);
    } else {
        linesEl.appendChild(line);
    }
    // Was the reader already at the bottom? Decided BEFORE the insert, because the insert itself
    // changes scrollHeight — and someone who scrolled up to read must not be yanked down by a new
    // arrival (Codex review 2026-09-16).
    const following = linesEl.scrollHeight - linesEl.scrollTop - linesEl.clientHeight < 48;
    trim();
    // A row that the trim just evicted gets no animation and no timer: scheduling one would hold a
    // detached node alive for the whole fade window.
    if (!line.isConnected) return null;
    window.requestAnimationFrame(() => { if (line.isConnected) line.classList.add('is-shown'); });
    scheduleExpiry(line);
    if (following) linesEl.scrollTop = linesEl.scrollHeight;
    return line;
}

/** A local-only line: /help output, an unknown command, a failed send. Never leaves this browser. */
function notice(text: string): void {
    appendLine((line) => {
        line.classList.add('is-notice');
        text.split('\n').forEach((part, index) => {
            if (index > 0) line.appendChild(document.createElement('br'));
            line.appendChild(document.createTextNode(part));
        });
    }, 'notice');
}

function avatarEl(speaker: ChatSpeaker): HTMLElement {
    const avatar = document.createElement('span');
    avatar.className = 'ft3d-chat__avatar';
    const url = String(speaker.pic_url ?? '').trim();
    if (url) {
        const img = document.createElement('img');
        img.src = url;
        img.alt = '';
        avatar.appendChild(img);
    } else {
        avatar.classList.add('is-initials');
        avatar.textContent = (speaker.name ?? '?').trim().slice(0, 2).toUpperCase();
    }
    return avatar;
}

/** The one place a message becomes a line — from our own send response or from the socket. */
export function deliverChatMessage(message: FtChatMessage, opts: { history?: boolean } = {}): boolean {
    if (!linesEl || message?.schema !== CHAT_SCHEMA) return false;
    const id = String(message.message_id ?? '').trim().toUpperCase();
    if (!id || seen.has(id)) return false;

    // Validate BEFORE anything is shown or remembered: an unknown channel must not fall through to
    // `say` styling and the public-bubble path, and a line that cannot be rendered must not burn its
    // id (the socket copy of the same message would then be swallowed too).
    const channel = String(message.channel ?? '');
    if (!(CHANNELS as readonly string[]).includes(channel)) return false;
    const viewer = (window as unknown as { FateTide?: { user?: { id?: string; isGM?: boolean } } }).FateTide?.user;
    if (!chatVisibleToViewer(message, String(viewer?.id ?? ''), viewer?.isGM === true, campaignId(), encounterId())) return false;
    const speaker = message.speaker ?? {};
    const name = String(speaker.name ?? 'Someone');
    const text = String(message.text ?? '');
    // A room line that belongs to an encounter this page is not showing is not ours to display.
    const here = encounterId().toUpperCase();
    if (BUBBLE_CHANNELS.includes(channel) && here && message.encounter_id
        && String(message.encounter_id).toUpperCase() !== here) return false;
    const line = appendLine((el) => {
        el.appendChild(avatarEl(speaker));
        // Real spaces around the parts, so the line's textContent reads as a sentence for a screen
        // reader (the initials avatar would otherwise run straight into the name: "COCodex Tester").
        el.appendChild(document.createTextNode(' '));
        const who = document.createElement('span');
        who.className = 'ft3d-chat__name';
        if (channel === 'whisper' || channel === 'gm') {
            const mine = String(message.from_user_id ?? '').toUpperCase()
                === String((window as { FateTide?: { user?: { id?: string } } }).FateTide?.user?.id ?? '').toUpperCase();
            const other = String(message.recipient_name ?? 'the GM');
            who.textContent = mine ? `To ${other}:` : `${name} whispers:`;
        } else if (channel === 'emote') {
            who.textContent = name;
        } else {
            who.textContent = `${name}${channel === 'yell' ? ' yells:' : channel === 'ooc' ? ' (OOC):' : ':'}`;
        }
        el.appendChild(who);
        // A real space, not the flex gap: the line's textContent is what a screen reader announces
        // and what a smoke asserts on, and "Delion:hello" reads wrong in both.
        el.appendChild(document.createTextNode(' '));
        const body = document.createElement('span');
        body.className = 'ft3d-chat__text';
        body.textContent = text;
        el.appendChild(body);
    }, channel, opts.history === true);
    // Remembered once the message has been HANDLED — rendered, or immediately evicted by the trim
    // because the transcript was full. Either way the socket copy of it is a duplicate. (Only a
    // genuine render failure leaves the id unseen, so that copy still gets its chance.)
    seen.add(id);
    if (!line) return false;
    if (opts.history) line.classList.add('is-history');
    if (seen.size > 400) seen.delete(seen.values().next().value!);

    // The bubble over the speaker's token, and the emote's performance. Neither belongs to history: a
    // reload must not re-shout everything said before it — and neither may fail the delivery. The
    // performance is usually ALREADY done by now (the sender's own keypress, or the `ft3d_emote_cue`
    // frame that is pushed ahead of this line); performEmote() dedupes it by the shared id.
    if (!opts.history) {
        try { postBubble(message); } catch { /* presentation only */ }
        const emote = String(message.emote ?? '').trim();
        if (emote) {
            try {
                performEmote({
                    id,
                    entityId: String(message.speaker?.entity_id ?? ''),
                    emote,
                    voice: message.speaker?.voice ?? null,
                    via: 'chat-line',
                    dances: Array.isArray(message.dances) ? message.dances : [],
                });
            } catch { /* the words already landed */ }
        }
    }
    return true;
}

/** The scene iframe draws the bubble; it owns token placement and what this viewer can perceive. */
function postBubble(message: FtChatMessage): void {
    const entityId = String(message.speaker?.entity_id ?? '').trim();
    const channel = String(message.channel ?? '');
    // ALLOW-list: only the in-character room channels speak from a token. A private line, an OOC aside
    // and anything unrecognised draw nothing.
    if (!entityId || !BUBBLE_CHANNELS.includes(channel)) return;
    const frame = document.querySelector<HTMLIFrameElement>('[data-ft3d-encounter-frame]');
    if (!frame?.contentWindow) return;
    frame.contentWindow.postMessage({
        type: 'ft3d-campaign-chat-bubble-v1',
        payload: {
            schema: 'ft.ft3d.chat-bubble.v1',
            messageId: String(message.message_id ?? ''),
            entityId: entityId.toUpperCase(),
            channel,
            text: String(message.text ?? '').slice(0, 255),
            speakerName: String(message.speaker?.name ?? ''),
        },
    }, window.location.origin);
}

// ── the emote PERFORMANCE (owner 2026-09-16: "100% JS/TS WS push") ─────────────────────────────────────
// An emote is performed from whichever of three things reaches this page first, and only once:
//   1. the SENDER's own keypress — before the request even leaves (send());
//   2. the `ft3d_emote_cue` frame, which the server pushes before it writes the line to the log;
//   3. the `ft3d_chat` line itself — the fallback for a page that missed the cue.
// All three carry the same id (the sender's browser mints it), so the performance dedupes on it. The log
// line is a passive upload; nothing about the gesture or the voice waits for it.

/** How long the voice waits for the scene to confirm the gesture started before speaking anyway. */
const GESTURE_ACK_MS = 250;
/** Ids already performed. Bounded generously: a line can only re-perform after this many NEWER emotes. */
const PERFORMED_CAP = 5000;
const performed = new Set<string>();
const gestureAcks = new Map<string, (outcome: GestureOutcome) => void>();

interface EmotePerformance {
    id: string;
    entityId: string;
    emote: string;
    /**
     * The server's voice slug — null means the speaker is voiceless or silenced, and stays silent. Only the
     * sender's own keypress, which performs before the server has answered, passes 'local' to resolve the slug
     * from this page's roster.
     */
    voice: string | null | 'local';
    via: 'keypress' | 'cue' | 'chat-line';
    /** /dance: the SPEAKER's playlist. Every screen chains the same dances. */
    dances?: readonly string[];
}

/**
 * The performer's dance playlist from this page's caster cache ([] = none set), or null when the page
 * does not hold that character's blob — then only the server's cue can say which dances to chain.
 */
function localDancePlaylist(entityId: string): string[] | null {
    const cache = (window as { FateTide?: { casterCache?: { get?: (id: string) => unknown } } }).FateTide?.casterCache;
    const caster = (cache?.get?.(entityId) ?? cache?.get?.(entityId.toLowerCase())) as { action_bars?: unknown; action_bar_settings?: { dance_playlist?: unknown } } | undefined;
    if (!caster) return null;
    const list = caster.action_bar_settings?.dance_playlist;
    return Array.isArray(list) ? list.map(String) : [];
}

function newCueId(): string {
    const c = globalThis.crypto;
    if (typeof c?.randomUUID === 'function') return c.randomUUID().toUpperCase();
    const bytes = new Uint8Array(16);
    c.getRandomValues(bytes);
    bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
    bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
    const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`.toUpperCase();
}

/**
 * The BODY of an emote: the scene plays the matching avatar clip and answers with a receipt, which is
 * the voice's start signal — so the bark lands on the gesture's first frame instead of whenever the
 * network delivered it. Resolves on that receipt, or after GESTURE_ACK_MS when there is no scene or it
 * does not answer. The scene refuses to animate an avatar this viewer cannot perceive and still answers;
 * the voice plays either way (owner 2026-09-16: sound carries where sight does not).
 */
/** How a gesture settled: the scene's receipt (with the clip it played, or none), a timeout, or no scene at all. */
type GestureOutcome = { receipt: true; played: string | null } | { receipt: false; reason: 'timeout' | 'no-scene' };

function postEmoteCue(id: string, entityId: string, emote: string, dances: readonly string[] = []): Promise<GestureOutcome> {
    const frame = document.querySelector<HTMLIFrameElement>('[data-ft3d-encounter-frame]');
    if (!entityId || !frame?.contentWindow) return Promise.resolve({ receipt: false, reason: 'no-scene' });
    return new Promise<GestureOutcome>((resolve) => {
        const done = (outcome: GestureOutcome): void => {
            if (!gestureAcks.has(id)) return;
            gestureAcks.delete(id);
            resolve(outcome);
        };
        gestureAcks.set(id, done);
        window.setTimeout(() => done({ receipt: false, reason: 'timeout' }), GESTURE_ACK_MS);
        frame.contentWindow!.postMessage({
            type: 'ft3d-campaign-emote-cue-v1',
            payload: {
                schema: 'ft.ft3d.emote-cue.v1',
                cueId: id,
                entityId: entityId.toUpperCase(),
                emote,
                // The SPEAKER's dance playlist — never this viewer's own (owner 2026-09-16, the dance picker).
                dances: Array.from(dances, String),
            },
        }, window.location.origin);
    });
}

/** Perform one emote — gesture, then its voice on the gesture's receipt — at most once per id. */
function performEmote(cue: EmotePerformance): void {
    const id = cue.id.trim().toUpperCase();
    if (!id || !cue.emote || performed.has(id)) return;
    performed.add(id);
    if (performed.size > PERFORMED_CAP) performed.delete(performed.values().next().value!);
    const entityId = cue.entityId.trim().toUpperCase();
    const at = performance.now();
    type Prepare = (voice: string | null, entityId: string, emote: string, seed: string, resolveLocally: boolean) => Promise<(() => void) | null>;
    const prepare = (window as { ftPrepareChatEmoteVoice?: Prepare }).ftPrepareChatEmoteVoice;
    // Both start NOW, in parallel: the take resolves while the scene starts the clip.
    const local = cue.voice === 'local';
    const voiceReady = prepare && (local || cue.voice)
        ? prepare(local ? null : cue.voice, entityId, cue.emote, id, local)
        : Promise.resolve(null);
    const gestureStarted = postEmoteCue(id, entityId, cue.emote, cue.dances ?? []);
    void Promise.all([voiceReady, gestureStarted]).then(([play, gesture]) => {
        play?.();
        ftChatDebug({ emote: cue.emote, id, via: cue.via, voiced: !!play, gesture, voiceAtMs: Math.round(performance.now() - at) });
    });
    ftChatDebug({ emote: cue.emote, id, via: cue.via, performed: true });
}

/** The scene's receipt for a gesture it was asked to start (encounter-creation-engine/index.ts). */
function onSceneMessage(event: MessageEvent): void {
    const data = event.data as { type?: string; cueId?: unknown; played?: unknown } | null;
    if (data?.type !== 'ft3d-campaign-emote-cue-result-v1' || event.origin !== window.location.origin) return;
    const frame = document.querySelector<HTMLIFrameElement>('[data-ft3d-encounter-frame]');
    if (!frame?.contentWindow || event.source !== frame.contentWindow) return;
    gestureAcks.get(String(data.cueId ?? '').toUpperCase())?.({
        receipt: true,
        played: typeof data.played === 'string' ? data.played : null,
    });
}

/** The pushed cue (websocket/ft3d-chat.ts → `ft:ft3d-emote-cue`), for this page's campaign and room only. */
function onEmoteCue(event: Event): void {
    const cue = (event as CustomEvent).detail as {
        cue_id?: string; campaign_id?: string; encounter_id?: string; entity_id?: string; emote?: string; voice?: string | null;
        dances?: unknown;
    } | null;
    if (!cue) return;
    const campaign = campaignId().toUpperCase();
    if (campaign && String(cue.campaign_id ?? '').toUpperCase() !== campaign) return;
    const here = encounterId().toUpperCase();
    if (here && String(cue.encounter_id ?? '').toUpperCase() !== here) return;
    performEmote({
        id: String(cue.cue_id ?? ''),
        entityId: String(cue.entity_id ?? ''),
        emote: String(cue.emote ?? ''),
        voice: cue.voice ?? null,
        via: 'cue',
        dances: Array.isArray(cue.dances) ? cue.dances.map(String) : [],
    });
}

function setChannel(channel: FtChatChannel, target = ''): void {
    stickyChannel = channel;
    stickyTarget = target;
    if (chipEl) chipEl.textContent = channelLabel(channel, target);
}

function setOpen(next: boolean, prefill?: string): void {
    open = next;
    root?.classList.toggle('is-open', next);
    if (!inputEl) return;
    if (next) {
        if (prefill !== undefined) inputEl.value = prefill;
        inputEl.focus();
        const end = inputEl.value.length;
        inputEl.setSelectionRange(end, end);
        if (linesEl) linesEl.scrollTop = linesEl.scrollHeight;
        // Anything mid-fade is being read again; bring it back.
        chatLinesInStream().forEach((line) => {
            line.classList.remove('is-leaving');
            line.classList.add('is-shown');
        });
    } else {
        inputEl.value = '';
        historyIndex = -1;
        inputEl.blur();
        // ⛔ RE-ARM every restored line. Opening chat brings faded lines back, and their original
        // timers have already run — without this they sit on screen for the rest of the session.
        chatLinesInStream().forEach((line) => {
            if (!line.classList.contains('is-shown')) return;
            scheduleExpiry(line);
        });
        // Eviction was deferred while they were reading; catch up now that they are done.
        trim();
    }
}

async function send(): Promise<void> {
    if (!inputEl) return;
    const raw = inputEl.value;
    const parsed = parseChatInput(raw, stickyChannel, rosterNames(), stickyTarget);
    if (parsed.notice) { notice(parsed.notice); inputEl.value = ''; return; }
    if (parsed.error) { notice(parsed.error); return; }
    if (!parsed.intent) { setOpen(false); return; }

    // Resolve the context BEFORE the field is cleared, or a send during page start-up eats the draft.
    const campaign_id = campaignId();
    const encounter_id = encounterId();
    if (!campaign_id || !encounter_id) { notice('Chat needs an open encounter.'); return; }

    let speech: Record<string,unknown>|undefined;
    const speechActor=activeCasterId()||emoteSpeakerId();
    if(BUBBLE_CHANNELS.includes(parsed.intent.channel)&&speechActor){
        try{
            const frame=document.querySelector<HTMLIFrameElement>('[data-ft3d-encounter-frame]');
            type SpeechHost=Window&{ft3dSpeechPlan?:(id:string,eid:string,cid:string)=>Record<string,unknown>|Promise<Record<string,unknown>>};
            const plan=(frame?.contentWindow as SpeechHost|null)?.ft3dSpeechPlan ?? (window as SpeechHost).ft3dSpeechPlan;
            if(!plan)throw new Error('speech:browser-not-loaded');
            speech=await plan(speechActor,encounter_id,campaign_id);
            if(Array.isArray(speech.reveals)&&speech.reveals.length){
                const tails=(window as Window&{ftGeometryStatusExitSkips?:(eid:string,rows:unknown[])=>string[]}).ftGeometryStatusExitSkips;
                if(!tails)throw new Error('speech:status-boundary-not-loaded');
                speech.logs=tails(encounter_id,speech.reveals);
            }
        }catch(error){notice(error instanceof Error?error.message:'Speech could not be resolved.');return;}
    }
    const trimmed = raw.trim();
    if (trimmed && sentHistory[sentHistory.length - 1] !== trimmed) {
        sentHistory.push(trimmed);
        if (sentHistory.length > 30) sentHistory.shift();
        try { window.localStorage.setItem(HISTORY_KEY, JSON.stringify(sentHistory.slice(-30))); } catch { /* per-viewer nicety */ }
    }
    historyIndex = -1;
    recallDraft = '';
    inputEl.value = '';
    // A plain line keeps speaking on the channel it was sent on, like WoW's sticky channel; a whisper
    // stays pointed at the same person until the player changes it.
    setChannel(parsed.intent.channel, parsed.intent.target ?? '');

    // A voiced emote is PERFORMED NOW, from this browser, before the request leaves: the id minted here
    // travels with the send, so the server's cue frame and the log line both dedupe against this
    // performance on every other client. The speaker is who this page is playing — the same caster_id
    // the send carries, or the server's own fallback when the page has none (emoteSpeakerId).
    const cueId = parsed.intent.emote ? newCueId() : '';
    const caster = activeCasterId();
    const performer = cueId ? emoteSpeakerId() : '';
    // A /dance needs the performer's PLAYLIST, which this page only knows when the performer's caster blob is
    // cached here; without it the pushed cue (which carries the server's copy) performs instead.
    const localDances = parsed.intent.emote === 'dance' ? localDancePlaylist(performer) : [];
    if (cueId && performer && localDances !== null) {
        try {
            performEmote({ id: cueId, entityId: performer, emote: parsed.intent.emote!, voice: 'local', via: 'keypress', dances: localDances });
        } catch { /* the line still goes out */ }
    }

    try {
        const response = await fetch(`${(window as { BASE_URL?: string }).BASE_URL || '/'}options/game/encounter/ft3d_chat_inc.php`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
                action: 'send',
                relay: false,
                campaign_id,
                encounter_id,
                caster_id: speechActor || caster,
                ...(speech ? {speech} : {}),
                channel: parsed.intent.channel,
                text: parsed.intent.text,
                ...(parsed.intent.emote ? { emote: parsed.intent.emote, message_id: cueId } : {}),
                ...(parsed.intent.target ? { target: parsed.intent.target } : {}),
            }),
        });
        const payload = await response.json().catch(() => ({})) as { ok?: boolean; error?: string; message?: FtChatMessage };
        if (!response.ok || !payload.ok || !payload.message) throw new Error(payload.error || 'Message not sent.');
        const publish = (window as Window & { ftPublishSavedChat?: (message: FtChatMessage, actor: string, speech?: Record<string, unknown>) => Promise<void> }).ftPublishSavedChat;
        if (!publish) throw new Error('chat:transport-not-loaded');
        await publish(payload.message, speechActor || caster, speech);
        deliverChatMessage(payload.message);
    } catch (error) {
        notice(error instanceof Error ? error.message : 'Message not sent.');
        // Hand the words back rather than eating them — a refusal is usually "wait a second" or a
        // mistyped whisper target, and retyping a lost line is the worst part of a chat that fails.
        if (inputEl && inputEl.value.trim() === '') {
            inputEl.value = raw;
            setOpen(true);
        }
    }
}

function recall(direction: 1 | -1): void {
    if (!inputEl || sentHistory.length === 0) return;
    // ↓ before ↑ does nothing: there is nothing newer than what is already in the field. (It used to
    // land on index 0 and recall the OLDEST line.)
    if (historyIndex === -1) {
        if (direction === -1) return;
        // Entering history — keep the unsent draft so walking back past the newest entry returns it.
        recallDraft = inputEl.value;
        historyIndex = sentHistory.length;
    }
    historyIndex = Math.min(sentHistory.length, Math.max(0, historyIndex - direction));
    inputEl.value = historyIndex >= sentHistory.length ? recallDraft : (sentHistory[historyIndex] ?? '');
    const end = inputEl.value.length;
    inputEl.setSelectionRange(end, end);
}

/**
 * History, once the page knows which encounter it is in. At mount the ids can still be empty — the
 * shell publishes its frame-derived encounter id during its own DOMContentLoaded pass — so this waits
 * for them rather than silently opening an empty transcript.
 */
async function loadHistory(attempt = 0): Promise<void> {
    const campaign_id = campaignId();
    const encounter_id = encounterId();
    if (!campaign_id || !encounter_id) {
        if (attempt < 8) window.setTimeout(() => { void loadHistory(attempt + 1); }, 750);
        return;
    }
    try {
        const response = await fetch(`${(window as { BASE_URL?: string }).BASE_URL || '/'}options/game/encounter/ft3d_chat_inc.php`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ action: 'history', campaign_id, encounter_id }),
        });
        const payload = await response.json().catch(() => ({})) as { ok?: boolean; messages?: FtChatMessage[] };
        if (!payload.ok || !Array.isArray(payload.messages)) return;
        payload.messages.forEach(message => deliverChatMessage(message, { history: true }));
    } catch { /* history is a convenience; live chat still works without it */ }
}

/**
 * Opened from the shell's Enter binding (my_campaign.php) as well as from the strip itself. An
 * already-open bar keeps whatever is half-typed in it — re-opening must never eat a draft.
 */
function openChat(prefill?: string): void {
    setOpen(true, open ? undefined : prefill);
}

/**
 * The channel picker the chip opens — WoW's list of what a slash does, as a menu. Choosing a channel
 * arms it (a whisper prefills `/w ` so the player types the name); choosing an emote performs it now.
 */
function renderMenu(): void {
    if (!menuEl) return;
    menuEl.textContent = '';
    const { channels, emotes } = chatLegend();
    const section = (title: string, entries: ReturnType<typeof chatLegend>['channels']): void => {
        const heading = document.createElement('p');
        heading.className = 'ft3d-chat__menu-heading';
        heading.textContent = title;
        menuEl!.appendChild(heading);
        entries.forEach((entry) => {
            const row = document.createElement('button');
            row.type = 'button';
            row.className = 'ft3d-chat__menu-row';
            row.setAttribute('role', 'menuitem');
            const cmd = document.createElement('span');
            cmd.className = 'ft3d-chat__menu-cmd';
            cmd.textContent = entry.command;
            const label = document.createElement('span');
            label.className = 'ft3d-chat__menu-label';
            label.textContent = entry.description;
            // A real space between the command and what it does — the row's textContent is what a
            // screen reader announces ("/laughLaugh" otherwise).
            row.append(cmd, document.createTextNode(' '), label);
            row.addEventListener('click', () => {
                closeMenu();
                if (entry.emote) {
                    // An emote is a whole line on its own — perform it rather than arm it.
                    if (inputEl) inputEl.value = entry.command;
                    void send();
                    return;
                }
                if (entry.needsTarget) {
                    setOpen(true, `${entry.command} `);
                    return;
                }
                if (entry.channel) setChannel(entry.channel);
                setOpen(true);
            });
            menuEl!.appendChild(row);
        });
    };
    section('Channels', channels);
    section('Emotes', emotes);
}

function closeMenu(): void {
    if (!menuEl) return;
    menuEl.hidden = true;
    chipEl?.setAttribute('aria-expanded', 'false');
}

function toggleMenu(): void {
    if (!menuEl) return;
    const next = menuEl.hidden;
    // Show FIRST, then fill: if building a row ever throws, the player still gets an (empty) menu they
    // can close, instead of a chip that silently does nothing.
    menuEl.hidden = !next;
    chipEl?.setAttribute('aria-expanded', next ? 'true' : 'false');
    if (next) {
        try {
            renderMenu();
        } catch (error) {
            menuEl.textContent = 'The command list could not be built — type /help instead.';
            ftChatDebug({ menu: 'render-failed', error: String(error) });
        }
    }
}

export function mountFt3dChat(host: HTMLElement): void {
    root = host.querySelector<HTMLElement>('[data-ft3d-chat]');
    if (!root) return;
    // Speech shares the LOG's line stream (owner 2026-09-16) so events and talk interleave in time;
    // the feed's tabs decide which kinds are shown.
    linesEl = ft3dLogLineHost();
    inputEl = root.querySelector<HTMLInputElement>('[data-ft3d-chat-input]');
    chipEl = root.querySelector<HTMLElement>('[data-ft3d-chat-channel]');
    menuEl = root.querySelector<HTMLElement>('[data-ft3d-chat-menu]');
    feedEl = host.querySelector<HTMLElement>('[data-ft3d-feed]');
    setChannel('say');

    ftChatDebug({ mount: true, chip: !!chipEl, menu: !!menuEl, stream: !!linesEl, input: !!inputEl });
    chipEl?.addEventListener('click', (event) => {
        event.stopPropagation();
        ftChatDebug({ chipClick: true });
        toggleMenu();
    });
    document.addEventListener('click', (event) => {
        if (!menuEl || menuEl.hidden) return;
        const target = event.target instanceof Node ? event.target : null;
        if (target && (menuEl.contains(target) || chipEl?.contains(target))) return;
        closeMenu();
    });

    try {
        const stored = JSON.parse(window.localStorage.getItem(HISTORY_KEY) ?? '[]') as unknown;
        if (Array.isArray(stored)) stored.filter((v): v is string => typeof v === 'string').forEach(v => sentHistory.push(v));
    } catch { /* a blocked store only costs the ↑ recall */ }

    inputEl?.addEventListener('keydown', (event) => {
        // The field owns its keys — the shell's hotkeys already skip a focused input, and stopping
        // propagation here keeps a typed "w" from also walking the camera.
        event.stopPropagation();
        // An IME is still composing: Enter here CONFIRMS the word, it does not send the line.
        if (event.isComposing || event.keyCode === 229) return;
        if (event.key === 'Enter') {
            event.preventDefault();
            // Enter sends AND closes, like WoW: the keys go straight back to the game, and — because an
            // open bar deliberately holds every line on screen for the reader — a bar left open was why
            // a sent line appeared to never fade (owner 2026-09-16). send() has already read the field.
            void send();
            setOpen(false);
            return;
        }
        if (event.key === 'Escape') {
            event.preventDefault();
            setOpen(false);
            return;
        }
        if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
            event.preventDefault();
            recall(event.key === 'ArrowUp' ? 1 : -1);
        }
    });
    inputEl?.addEventListener('blur', () => { if (inputEl && inputEl.value.trim() === '') setOpen(false); });
    root.querySelector<HTMLElement>('[data-ft3d-chat-open]')?.addEventListener('click', () => openChat());
    root.querySelector<HTMLElement>('[data-ft3d-chat-help]')?.addEventListener('click', () => notice(chatHelpText()));

    window.addEventListener('ft:ft3d-chat', (event) => {
        const detail = (event as CustomEvent).detail as FtChatMessage | null;
        if (!detail) return;
        const here = campaignId().toUpperCase();
        if (here && String(detail.campaign_id ?? '').toUpperCase() !== here) return;
        deliverChatMessage(detail);
    });
    window.addEventListener('ft:ft3d-emote-cue', onEmoteCue);
    window.addEventListener('message', onSceneMessage);

    (window as { ftFt3dChatOpen?: (prefill?: string) => void }).ftFt3dChatOpen = openChat;
    (window as { ftFt3dChatIsOpen?: () => boolean }).ftFt3dChatIsOpen = () => open;

    void loadHistory();
}
